import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { appModel, businessModel, engineCapabilities, revenueExpansion } from '../../data/deckSlide11';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3 | 4 | 5;
export function BusinessModelSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(700, 1); // engine
    at(1800, 2); // VEXU App model
    at(3100, 3); // VEXU Business model
    at(4400, 4); // business expansion path
    at(6400, 5); // revenue expansion + takeaway
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2111" data-mp="3cucg7igu43ak">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2112" data-mp="3ctt9yz0jr3cd">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 65% at 6% -18%, rgba(0,200,83,0.06) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2113" data-mp="3hsw4vqmtbt6m" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2114" data-mp="3vkaonzojnyeq">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2115" data-mp="3mq623sgg1950" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2116" data-mp="3qsj4q7zw8qxk">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2117" data-mp="ic7j070x9qwg">
          12 · Business Model
        </motion.span>

        {/* Headline */}
        <h1 className="absolute text-[40px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 126,
        width: 640
      }} data-id="element-2118" data-mp="nyhwt8easmyt">
          {['Recurring revenue.', 'Designed to expand.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.15 + i * 0.15,
          ease
        }} data-id="element-2119" data-mp="9an0zj4zu2hi" data-mpi={i}>
              {line}
            </motion.span>)}
        </h1>
        <motion.p className="absolute text-[16px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 236,
        width: 620
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.7,
        ease
      }} data-id="element-2120" data-mp="3rg9tv17ajx3v">
          VEXU monetizes individuals and businesses through recurring plans,
          with revenue growing through usage and added capabilities.
        </motion.p>

        {/* The shared engine */}
        <motion.div className="absolute text-center" style={{
        left: 730,
        top: 296,
        width: 140
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(1) ? 1 : 0,
        y: shown(1) ? 0 : 10
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2121" data-mp="3r9zgornfzq5z">
          <div className="mx-auto grid h-11 w-11 place-items-center rounded-full" style={{
          backgroundColor: 'rgba(0,200,83,0.10)'
        }} data-id="element-2122" data-mp="33l6dqb8eki1v">
            <img src={VEXU_LOGO} alt="" className="h-6 w-6 object-contain" data-id="element-2123" data-mp="2awsghxp51prd" />
          </div>
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-vexu-700" data-id="element-2124" data-mp="2fvvnhnooi61z">
            One AI engine
          </p>
        </motion.div>
        <motion.p className="absolute text-center text-[11.5px] text-[#A3AFA7]" style={{
        left: 620,
        top: 402,
        width: 360
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(1) ? 1 : 0
      }} transition={{
        duration: 0.8,
        delay: 0.3,
        ease
      }} data-id="element-2125" data-mp="lgezlk1ncqhl">
          {engineCapabilities}
        </motion.p>

        {/* Branches */}
        <svg className="absolute" style={{
        left: 200,
        top: 428,
        width: 1200,
        height: 76
      }} viewBox="0 0 1200 76" fill="none" aria-hidden="true" data-id="element-2126" data-mp="145d9prdcoyy0">
          <motion.path d="M600 4 C600 42, 108 34, 108 72" stroke="rgba(0,200,83,0.4)" strokeWidth="1" initial={{
          pathLength: 0
        }} animate={{
          pathLength: shown(2) ? 1 : 0
        }} transition={{
          duration: 0.9,
          ease
        }} data-id="element-2127" data-mp="1e39kgi2dlrvl" />
          <motion.path d="M600 4 C600 42, 700 34, 700 72" stroke="rgba(0,200,83,0.4)" strokeWidth="1" initial={{
          pathLength: 0
        }} animate={{
          pathLength: shown(3) ? 1 : 0
        }} transition={{
          duration: 0.9,
          ease
        }} data-id="element-2128" data-mp="3axe4uzvq3jn8" />
        </svg>

        {/* LEFT — VEXU App */}
        <motion.div className="absolute" style={{
        left: 176,
        top: 508,
        width: 380
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(2) ? 1 : 0,
        y: shown(2) ? 0 : 10
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2129" data-mp="1goh1t0vhagx1">
          <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#0B1712]" data-id="element-2130" data-mp="1cnx1joflyx6o">
            {appModel.name}
          </p>
          <p className="mt-1.5 text-[12.5px] text-[#7C8B82]" data-id="element-2131" data-mp="xs92edp5d09n">
            {appModel.audience}
          </p>

          <p className="mt-5 text-[21px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2132" data-mp="2s3w6yrxucf38">
            {appModel.model.join(' + ')}
          </p>

          <div className="mt-4 flex items-center gap-2.5 text-[12.5px] text-[#7C8B82]" data-id="element-2133" data-mp="2x1pcrjv8f72z">
            {appModel.journey.map((step, i) => <React.Fragment key={step} data-id="element-2134" data-mp="2vjgxh6c9lvrq" data-mpi={i}>
                {i > 0 && <span className="text-[#C3CCC5]" data-id="element-2135" data-mp="2qlp3m6s27zkw">→</span>}
                <span data-id="element-2136" data-mp="2lnnoty1o2mf8">{step}</span>
              </React.Fragment>)}
          </div>

          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A3AFA7]" data-id="element-2137" data-mp="2hm23dob2gztf">
            Starting at
          </p>
          <p className="mt-1 text-[26px] font-semibold tracking-[-0.02em] text-vexu-700 tabular-nums" data-id="element-2138" data-mp="3eb9q2znf9eyx">
            {appModel.price}
            <span className="ml-1 text-[14px] font-medium text-[#7C8B82]" data-id="element-2139" data-mp="39e02zqni9fe9">
              / month
            </span>
          </p>
        </motion.div>

        {/* RIGHT — VEXU Business */}
        <motion.div className="absolute" style={{
        left: 768,
        top: 508,
        width: 640
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(3) ? 1 : 0,
        y: shown(3) ? 0 : 10
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2140" data-mp="30kepean2u46f">
          <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#0B1712]" data-id="element-2141" data-mp="1euhrhfgh2epz">
            {businessModel.name}
          </p>
          <p className="mt-1.5 text-[12.5px] text-[#7C8B82]" data-id="element-2142" data-mp="1juncics8l2z3">
            {businessModel.audience}
          </p>

          <p className="mt-5 text-[21px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2143" data-mp="1nwh5s003r4p8">
            {businessModel.model.join(' + ')}
          </p>

          <div className="mt-4 flex items-start gap-5" data-id="element-2144" data-mp="1xriwv1dxugug">
            {businessModel.expansion.map((step, i) => <React.Fragment key={step.stage} data-id="element-2145" data-mp="22pu4d5grgf9i" data-mpi={i}>
                {i > 0 && <motion.span className="mt-1 text-[11px] text-[#C3CCC5]" initial={{
              opacity: 0
            }} animate={{
              opacity: shown(4) ? 1 : 0
            }} transition={{
              duration: 0.4,
              delay: i * 0.28,
              ease
            }} data-id="element-2146" data-mp="27j6mqxuujptt">
                    →
                  </motion.span>}
                <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: shown(4) ? 1 : 0,
              y: shown(4) ? 0 : 6
            }} transition={{
              duration: 0.55,
              delay: i * 0.28,
              ease
            }} data-id="element-2147" data-mp="1wehf3n33v6it">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-vexu-700" data-id="element-2148" data-mp="21datdhq7v1k1">
                    {step.stage}
                  </p>
                  <p className="mt-1.5 text-[12.5px] leading-snug text-[#33463B]" data-id="element-2149" data-mp="1dm5dx3o6uba9">
                    {step.detail}
                  </p>
                </motion.div>
              </React.Fragment>)}
          </div>

          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A3AFA7]" data-id="element-2150" data-mp="1hq3d1edijm3b">
            Starting at
          </p>
          <p className="mt-1 text-[26px] font-semibold tracking-[-0.02em] text-vexu-700 tabular-nums" data-id="element-2151" data-mp="tz7fs1qafyoo">
            {businessModel.price}
            <span className="ml-1 text-[14px] font-medium text-[#7C8B82]" data-id="element-2152" data-mp="pwkpw6d6gnu4">
              / month
            </span>
          </p>
        </motion.div>

        {/* Revenue expansion */}
        <motion.div className="absolute flex items-center gap-4 border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        bottom: 112
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(5) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2153" data-mp="kmrsov80t6wz">
          {revenueExpansion.map((item, i) => <React.Fragment key={item} data-id="element-2154" data-mp="13kkxk327fqx0" data-mpi={i}>
              {i > 0 && <span className="text-[12px] text-[#C3CCC5]" data-id="element-2155" data-mp="13i8efmkpnaid">→</span>}
              <span className={`text-[14.5px] ${i === revenueExpansion.length - 1 ? 'font-semibold text-vexu-700' : 'text-[#5A6B60]'}`} data-id="element-2156" data-mp="om903u70ce4s">
                {item}
              </span>
            </React.Fragment>)}
        </motion.div>

        {/* Takeaway */}
        <motion.p className="absolute text-[15px] font-medium text-[#33463B]" style={{
        left: 112,
        bottom: 48
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(5) ? 1 : 0
      }} transition={{
        duration: 0.9,
        delay: 0.35,
        ease
      }} data-id="element-2157" data-mp="3ed5ks0qamp1i">
          Acquire customers once. Expand revenue through usage, capabilities and
          business adoption.
        </motion.p>
      </div>
    </SlideStage>;
}